import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography, useMediaQuery, Divider, Card, CardContent, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useAuth } from "../../context/authContext";
import Swal from "sweetalert2";
import "./Support.css";
import { useTranslation } from "react-i18next";
import { ALERT_MESSAGES, BUTTON, LABELS, TITLES, UI_TEXT } from "../../constants";
import CreateCaseForm from "../../components/ContactCCRForm/CreateCaseForm";
import { sendSupportEmail } from "../../services/supportService";
import { useSite } from "../../context/siteContext";
import { ConfirmationNumberRounded, Email, SupportAgentOutlined } from "@mui/icons-material";
import ContactCCRForm from "../../components/ContactCCRForm/ContactCCRForm";
import { useOptions } from "../../context/optionContext";
import {
	parseEmergencyPhones,
	resolveEmergencyNumberForRegion,
	resolveSupportSettings,
	SUPPORT_DEFAULT_REGION_PHONES,
} from "../../utilities/supportSettings";

const Support = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isMobile = useMediaQuery("(max-width:600px)");
	const isTablet = useMediaQuery("(max-width:1100px)");
	const { user, hasPermission } = useAuth();
	const { optionKeys } = useOptions();
	const canCreateCase = !!hasPermission("service_management.create_cases");
	const [ticketFormVisible, setTicketFormVisible] = useState(false);
	const [createCaseOpen, setCreateCaseOpen] = useState(false);
	const [statusFormVisible, setStatusFormVisible] = useState(false);
	const { selectedSite, selectedSiteData } = useSite();

	const [ccrDetails, setCCRDetails] = useState({
		ccr_name: "John Doe",
		ccr_phone: "+1 (804) 399-0051",
		ccr_email: "support@ithena.ai"
	});

	const [ramDetails, setRamDetails] = useState({
		ram_name: "",
		ram_mobile: "",
		ram_email: ""
	});

	const [emergencyContact, setEmergencyContact] = useState(SUPPORT_DEFAULT_REGION_PHONES);
	const [ccrOpen, setCcrOpen] = useState(false);

	const [formData, setFormData] = useState({
		email: user?.user_email || "",
		fullname: user?.user_display_name || "",
		phone: user?.user_phone || "",
		subject: "",
		message: "",
	});

	useEffect(() => {
		const supportSettings = resolveSupportSettings(optionKeys || {});
		if (!supportSettings.contactCcrShowEmergencyMessage) {
			setEmergencyContact([]);
			return;
		}
		const configured = parseEmergencyPhones(optionKeys?.customerLocationEmergencyPhones, SUPPORT_DEFAULT_REGION_PHONES);
		const resolvedSingle = resolveEmergencyNumberForRegion({
			source: supportSettings.contactCcrEmergencyNumberSource,
			manualNumber: supportSettings.contactCcrEmergencyNumberManual,
			userRegion: user?.region,
			phones: configured,
		});
		if (resolvedSingle) {
			setEmergencyContact([{ region: user?.region || "Emergency", phone: resolvedSingle }]);
			return;
		}
		if (!configured?.length) {
			setEmergencyContact([]);
			return;
		}
		if (!user || !user?.region) {
			setEmergencyContact(configured);
			return;
		}
		const userRegionPhone = (configured || []).filter((r) => r?.region === user?.region);
		if (userRegionPhone?.length) {
			setEmergencyContact(userRegionPhone);
		} else {
			setEmergencyContact(configured);
		}
	}, [user, optionKeys]);

	useEffect(() => {
		if (selectedSite && selectedSiteData) {
			setCCRDetails({
				ccr_name: selectedSiteData?.ccr_name || "",
				ccr_phone: selectedSiteData?.ccr_phone || "",
				ccr_email: selectedSiteData?.ccr_email || ""
			});

			setRamDetails({
				ram_name: selectedSiteData?.ram_name || "",
				ram_mobile: selectedSiteData?.ram_mobile || "",
				ram_email: selectedSiteData?.ram_email || ""
			});
		}
	}, [selectedSite, selectedSiteData]);

	const [supportFormData, setSupportFormData] = useState({
		ticket_number: "",
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const req_body = {
			name: formData.fullname,
			email: formData.email,
			phone: formData.phone,
			subject: formData.subject,
			message: formData.message,
		};
		try {
			const response = await sendSupportEmail(req_body);
			if (response.status) {
				setFormData({
					email: user?.user_email || "",
					fullname: user?.user_display_name || "",
					phone: user?.user_phone || "",
					subject: "",
					message: "",
				});
				Swal.fire({
					icon: "success",
					title: t("ALERT_MESSAGES.SUCCESS"),
					text: t("ALERT_MESSAGES.CUSTOMER_CARE_CONTACT_SHORTLY"),
				});
			}
		} catch (error) {
			
		}
	};

	const handleTicketFormOpen = () => {
		if (!canCreateCase) return;
		setCreateCaseOpen(true);
		setTicketFormVisible(false);
		setStatusFormVisible(false);
	};

	const handleStatusFormOpen = () => {
		setStatusFormVisible(true);
		setTicketFormVisible(false);
	};

	const getCardWidth = () => {
		if (isMobile) return "100%";
		if (isTablet) return "calc(50% - 12px)";
		return "calc(25% - 18px)";
	};

	const cardSx = {
		boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
		borderRadius: "12px",
		borderBottom: "2px solid",
		borderColor: (theme) => theme.palette.primary.main,
		backgroundColor: "#fff",
		width: getCardWidth(),
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		height: isMobile ? "auto" : "225px",
	};

	const cardContentSx = {
		p: 2,
		height: "100%",
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
	};

	const infoGroupSx = {
		display: "flex",
		justifyContent: "flex-start",
		alignItems: "flex-start",
		columnGap: 1,
		flexDirection: "column",
		rowGap: 1.5,
		width: "100%",
		flex: 1,
	};

	const infoRowSx = {
		display: "flex",
		justifyContent: "flex-start",
		gap: "2px"
	};

	const valueTextSx = {
		minWidth: 0,
		wordBreak: "break-word",
		overflowWrap: "anywhere",
		color: "#111827",
	};

	const actionButtonSx = {
		mt: "auto",
		alignSelf: "flex-start",
		minWidth: "160px",
		height: "38px",
		px: 2,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-start",
				width: "100%",
				maxWidth: "1200px",
				minHeight: "380px",
				height: "100%",
			}}
		>
			<PageTitle title={t("TITLES.SUPPORTS")} />

			<Box
				display="flex"
				flexDirection={isMobile ? "column" : "row"}
				gap={2}
				alignItems="stretch"
				justifyContent="flex-start"
				sx={{
					mt: 2,
					flexWrap: "wrap",
					width: "100%",
				}}
			>
				<Card sx={cardSx}>
					<CardContent sx={cardContentSx}>
						<Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
							{t("TITLES.CUSTOMER_CARE_CONTACT")}
						</Typography>
						<Box className="card-divider"></Box>
						<Box sx={infoGroupSx}>
							<Box sx={infoRowSx}>
								<Typography variant="body1" fontWeight="bold">{t("LABELS.NAME")}:</Typography>
								{ccrDetails?.ccr_name ? (
									<Typography variant="body1" sx={valueTextSx}>{ccrDetails?.ccr_name}</Typography>
								) : (
									<Typography variant="body2" color="text.secondary" fontSize="11px" fontStyle="italic" sx={valueTextSx}>
										{t("UI_TEXT.CCR_NAME_NOT_AVAILABLE")}
									</Typography>
								)}
							</Box>
							<Box sx={infoRowSx}>
								<Typography variant="body1" fontWeight="bold">
									{t("LABELS.PHONE")}:
								</Typography>

								{ccrDetails?.ccr_phone ? (
									<Link
										href={`tel:${ccrDetails?.ccr_phone}`}
										underline="hover"
										sx={{
											...valueTextSx,
											color: "var(--primary-main)",   // ✅ correct CSS variable usage
											textDecorationColor: "var(--primary-main)",
											fontWeight: 500,
										}}
									>
										{ccrDetails?.ccr_phone}
									</Link>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										fontSize="11px"
										fontStyle="italic"
										sx={valueTextSx}
									>
										{t("UI_TEXT.CCR_PHONE_NOT_AVAILABLE")}
									</Typography>
								)}
							</Box>

							<Box sx={infoRowSx}>
								<Typography variant="body1" fontWeight="bold">
									{t("TABLE_HEADERS.EMAIL")}:
								</Typography>

								{ccrDetails?.ccr_email ? (
									<Link
										href={`mailto:${ccrDetails?.ccr_email}`}
										underline="hover"
										sx={{
											...valueTextSx,
											color: "var(--primary-main)",   // ✅ correct
											textDecorationColor: "var(--primary-main)",
											fontWeight: 500,
										}}
									>
										{ccrDetails?.ccr_email}
									</Link>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										fontSize="11px"
										fontStyle="italic"
										sx={valueTextSx}
									>
										Email not available
									</Typography>
								)}
							</Box>

							<Button
								startIcon={<Email />}
								variant="contained"
								color="primary"
								sx={actionButtonSx}
								onClick={() => setCcrOpen(true)}
							>
								{t("LABELS.CONTACT_CCR")}
							</Button>
						</Box>
					</CardContent>
				</Card>

				<Card sx={cardSx}>
					<CardContent sx={cardContentSx}>
						<Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
							Regional Account Manager
						</Typography>
						<Box className="card-divider"></Box>
						<Box sx={infoGroupSx}>
							<Box sx={infoRowSx}>
								<Typography variant="body1" fontWeight="bold">{t("LABELS.NAME")}:</Typography>
								{ramDetails?.ram_name ? (
									<Typography variant="body1" sx={valueTextSx}>{ramDetails?.ram_name}</Typography>
								) : (
									<Typography variant="body2" color="text.secondary" fontSize="11px" fontStyle="italic" sx={valueTextSx}>
										Name not available
									</Typography>
								)}
							</Box>

							<Box sx={infoRowSx}>
								<Typography variant="body1" fontWeight="bold">{t("LABELS.PHONE")}:</Typography>
								{ramDetails?.ram_mobile ? (
									<Link
										href={`tel:${ramDetails?.ram_mobile}`}
										underline="hover"
										sx={{
											color: "--primary-main !important",
											textDecorationColor: "#111827",
										}}
									>
										{ramDetails?.ram_mobile}
									</Link>
								) : (
									<Typography variant="body2" color="text.secondary" fontSize="11px" fontStyle="italic" sx={valueTextSx}>
										Phone not available
									</Typography>
								)}
							</Box>

							<Box sx={infoRowSx}>
								<Typography variant="body1" fontWeight="bold">{t("TABLE_HEADERS.EMAIL")}:</Typography>
								{ramDetails?.ram_email ? (
									<Link
										href={`mailto:${ramDetails?.ram_email}`}
										underline="hover"
										sx={{
											color: "--primary-main !important",
											textDecorationColor: "#111827",
										}}
									>
										{ramDetails?.ram_email}
									</Link>
								) : (
									<Typography variant="body2" color="text.secondary" fontSize="11px" fontStyle="italic" sx={valueTextSx}>
										Email not available
									</Typography>
								)}
							</Box>
						</Box>
					</CardContent>
				</Card>

				<Card sx={cardSx}>
					<CardContent sx={cardContentSx}>
						<Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
							{t("GENERAL.EMERGENCY_CONTACT")}
						</Typography>
						<Box className="card-divider"></Box>
						<Box sx={infoGroupSx}>
							{emergencyContact?.length ? (
								(emergencyContact || []).map((c, index) => (
									<Box key={`${c?.region || "region"}-${index}`} sx={infoRowSx}>
										<Typography variant="body1" fontWeight="bold">
											{c?.region}:
										</Typography>

										<Link
											href={`tel:${c?.phone}`}
											underline="hover"
											sx={{
												...valueTextSx,
												color: "var(--primary-main)",
												textDecorationColor: "var(--primary-main)",
												fontWeight: 500,
											}}
										>
											{c?.phone}
										</Link>
									</Box>
								))
							) : (
								<Typography variant="body2" color="text.secondary" fontSize="11px" fontStyle="italic" sx={valueTextSx}>
									No Emergency Contacts available
								</Typography>
							)}
						</Box>
					</CardContent>
				</Card>

				<Card sx={{ ...cardSx, display: "flex", flexDirection: "column" }}>
					<CardContent sx={{ ...cardContentSx, flex: 1 }}>
						<Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
							{t("UI_TEXT.CREATE_A_NON_EMERGENCY_CASE", { defaultValue: "Create a Non-Emergency Case" })}
						</Typography>
						<Box className="card-divider"></Box>
						<Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, flex: 1 }}>
							{"Initiate a new case to track and manage your issue or support request efficiently"}
						</Typography>
						{canCreateCase ? (
							<Button
								startIcon={<ConfirmationNumberRounded />}
								variant="contained"
								color="primary"
								sx={actionButtonSx}
								onClick={handleTicketFormOpen}
							>
								{t("UI_TEXT.CREATE_CASE", { defaultValue: "Enter a Case" })}
							</Button>
						) : (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
								{"Please Contact CCR to create a Case"}
							</Typography>
						)}
					</CardContent>
				</Card>
			</Box>

			{canCreateCase && <CreateCaseForm open={createCaseOpen} setOpen={setCreateCaseOpen} />}
			<ContactCCRForm open={ccrOpen} setOpen={setCcrOpen} />
		</Box>
	);
};

export default Support;